import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { addDays, mondayOf } from 'shared/checkin-weeks';
import { StageRole, TrackerConfig } from 'shared/tracker-config';
import {
  fmtTrackerDate,
  stageByKey,
  TrackerPerson,
  TrackerTouch,
  TRACKER_OFF_MESSAGE,
} from 'shared/tracker-people';

export { touchesByPerson } from 'shared/tracker-people';

export type TrackerData = RpxResponse<typeof rpx.tracker.getMyTracker>;
export type Person = TrackerPerson;
export type Touch = TrackerTouch;
export type PersonResult = { person: Person; touches: Touch[] };

export const INPUT_CLASS =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[color-scheme:dark] focus:border-indigo-500 focus:ring-indigo-500';
/** A card on the Tracker tab: the scoreboard, the set-up form, Your bets. */
export const CARD_CLASS =
  'border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-5 flex flex-col gap-2 sm:gap-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
export const LABEL_CLASS = 'text-sm font-medium text-gray-800 dark:text-gray-200';
export const HELP_CLASS = 'text-xs text-gray-500 dark:text-gray-400';
export const LINK_CLASS =
  'text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 underline text-sm';

export type ServerError = { message: string; field?: string; statusCode?: number };

/** A Boom error's message and field, as the rpx client throws it. */
export function readError(err: unknown): ServerError {
  const { statusCode, message, data } = (err || {}) as {
    statusCode?: number;
    message?: string;
    data?: { field?: string };
  };
  return {
    message: message || 'Something went wrong. Try again.',
    field: data?.field,
    statusCode,
  };
}

export function isTrackerOff(err: unknown) {
  const e = readError(err);
  return e.statusCode === 404 && e.message === TRACKER_OFF_MESSAGE;
}

/** "This week · Mon, Nov 16 – Sun, Nov 22". */
export function weekCaption(today: string) {
  const start = mondayOf(today);
  return `This week · ${fmtTrackerDate(start, today)} – ${fmtTrackerDate(addDays(start, 6), today)}`;
}

export function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (
    (words[0]?.[0] ?? '') + (words.length > 1 ? words[words.length - 1][0] : '')
  ).toUpperCase();
}

const ROLE_COLORS: Partial<Record<StageRole | 'none', string>> = {
  none: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  contacted:
    'bg-sky-50 dark:bg-sky-950 text-sky-800 dark:text-sky-200 border-sky-200 dark:border-sky-800',
  replied:
    'bg-indigo-50 dark:bg-indigo-950 text-indigo-800 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800',
  booked:
    'bg-violet-50 dark:bg-violet-950 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800',
  held: 'bg-violet-50 dark:bg-violet-950 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800',
  decision:
    'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  won: 'bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800',
  replied_no:
    'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  lost: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
};

export function stageChipClass(config: TrackerConfig, stageKey?: string) {
  const role = stageByKey(config, stageKey)?.stage.role;
  return ROLE_COLORS[role ?? 'none'] ?? ROLE_COLORS.none!;
}

/** Swaps in a person and their touches returned by a write. */
export function applyPersonResult(data: TrackerData, result: PersonResult): TrackerData {
  const exists = data.people.some((p) => p.id === result.person.id);
  return {
    ...data,
    people: exists
      ? data.people.map((p) => (p.id === result.person.id ? result.person : p))
      : [...data.people, result.person],
    touches: [...data.touches.filter((t) => t.personId !== result.person.id), ...result.touches],
  };
}

export function removePerson(data: TrackerData, personId: string): TrackerData {
  return {
    ...data,
    people: data.people.filter((p) => p.id !== personId),
    touches: data.touches.filter((t) => t.personId !== personId),
  };
}

export function readLocal(key: string) {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return;
  }
}

export function writeLocal(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private windows and blocked storage: the default is fine.
  }
}

export function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** A soft highlight on a row that just changed, without scrolling to it. */
export function flashRow(personId: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-person-row="${personId}"]`);
    if (!el || !(el as HTMLElement).animate) {
      return;
    }
    (el as HTMLElement).animate(
      [{ backgroundColor: 'rgb(254 243 199)' }, { backgroundColor: 'transparent' }],
      { duration: 1500, easing: 'ease-out' },
    );
  });
}

export function focusRowAction(personId: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-person-action="${personId}"]`) as
      | HTMLElement
      | undefined;
    el?.focus({ preventScroll: true });
  });
}
